import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from "react";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import Footer from "component/footer/Footer";
import DisplayType from "utils/displayType";
import Banner from "component/banner/Banner";
import { InsightMenu } from "admin/type/type";
import SearchBar from "component/search/SearchBar";
import { NewsProps } from "types/news";
import { Pagination } from "@mui/material";
import DefaultButton from "admin/component/button/DefaultButton";
import { updateTitle } from "utils/commonUtil";
import { useLanguage } from "i18n/LanguageContext";
import { getNews as fetchNewsApi } from "lib/latticeApi";
import { toNewsProps } from "utils/newsMapper";
import InsightTabs from "./components/InsightTabs";
import NewsCard from "../../component/card/News";
import newsBanner from "../../assets/news_banner.png";
import newsBanner1920 from "../../assets/news_banner_1920.png";
import newsBanner2048 from "../../assets/news_banner_2048.png";
import NewsCategoryList from "./organism/NewsCategoryList";

const DESKTOP_LIMIT = 12;
const MOBILE_LIMIT = 8;

const News = () => {
  const mode = DisplayType();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [news, updateNews] = useState<NewsProps[]>([]);
  const [mobileAccumulated, setMobileAccumulated] = useState<NewsProps[]>([]);
  const [totalPage, setTotalPage] = useState<number>(0);

  // 쿼리스트링에서 파라미터 읽기
  const currentPage = Number(searchParams.get("page") || "1");
  const searchKeyword = searchParams.get("search") || "";
  const categoryParam = searchParams.get("category") || "";
  const selectedCategories = categoryParam
    ? categoryParam.split(",").filter(Boolean)
    : [];
  const limit = mode === "Mobile" ? MOBILE_LIMIT : DESKTOP_LIMIT;

  const updateParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      if (key !== "page") {
        next.delete("page");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const fetchNews = useCallback(async () => {
    try {
      const { news: data, pagination } = await fetchNewsApi({
        page: currentPage,
        limit,
        search: searchKeyword || undefined,
        category: categoryParam || undefined,
      });
      const mapped = data.map((row) => toNewsProps(row));
      updateNews(mapped);
      setTotalPage(pagination.total_pages);

      if (mode === "Mobile" && currentPage > 1) {
        setMobileAccumulated((prev) => [...prev, ...mapped]);
      } else {
        setMobileAccumulated(mapped);
      }
    } catch (error) {
      console.log(error);
    }
  }, [currentPage, limit, searchKeyword, categoryParam, mode]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  useLayoutEffect(() => {
    updateTitle(t("insight", "news.pageTitle"));
  }, [t]);

  const displayList = mode === "Mobile" ? mobileAccumulated : news;

  return (
    <Container>
      <Banner
        bannerImg={newsBanner}
        bannerImg1920={newsBanner1920}
        bannerImg2048={newsBanner2048}
        mode={mode}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: mode === "Mobile" ? "center" : "flex-start",
          }}
        >
          <div
            className={`${
              mode === "Mobile" ? "font-title-en" : "font-title-en"
            }`}
            style={{ color: "var(--white)" }}
          >
            NEWS
          </div>
        </div>
      </Banner>
      <InsightTabs selectedTab={InsightMenu.NEWS} />
      <InsightBodyContainer className="limit__body">
        <SearchBar
          initValue={searchKeyword}
          onChage={() => {}}
          onSearching={(value) => {
            updateParam("search", value || "");
          }}
        />
        <NewsCategoryList
          selectedCategories={selectedCategories}
          onSelect={(categoryName) => {
            const next = selectedCategories.includes(categoryName)
              ? selectedCategories.filter((c) => c !== categoryName)
              : [...selectedCategories, categoryName];
            updateParam("category", next.join(","));
          }}
          onSelectAll={() => updateParam("category", "")}
        />
        <div className="news__list">
          {displayList.map((item) => {
            return <NewsCard key={item.hpNewsId} news={item} />;
          })}
          {displayList.length === 0 && (
            <SearchResultEmpty>
              {mode === "Mobile" ? (
                <div className="font-body3" style={{ textAlign: "center" }}>
                  {searchKeyword.length > 0 &&
                    `"${searchKeyword}" ${t("insight", "news.noResultFor")} `}
                  <br />
                  {t("insight", "news.noResult")}
                </div>
              ) : (
                <span className="font-body3">
                  {searchKeyword.length > 0 &&
                    `"${searchKeyword}" ${t("insight", "news.noResultFor")}`}
                  {` ${t("insight", "news.noResult")}`}
                </span>
              )}
            </SearchResultEmpty>
          )}
        </div>
        {mode !== "Mobile" && totalPage > 1 && (
          <div className="center">
            <Pagination
              count={totalPage}
              shape="rounded"
              page={currentPage}
              onChange={(event, page) => {
                updateParam("page", String(page));
              }}
            />
          </div>
        )}
        {mode === "Mobile" && totalPage > 0 && currentPage < totalPage && (
          <div className="center">
            <DefaultButton
              className="font-body3"
              type="outline"
              onClick={() => {
                updateParam("page", String(currentPage + 1));
              }}
            >
              {t("insight", "news.moreNews")}
            </DefaultButton>
          </div>
        )}
      </InsightBodyContainer>
      <Footer />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: 768px) {
  }
`;
const InsightBodyContainer = styled.div`
  flex-grow: 1;
  width: 100%;
  display: flex;
  padding-top: 100px;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;

  .news__list {
    display: flex;
    flex-direction: column;
    margin-top: 56px;
    margin-bottom: 56px;
    gap: 24px;
    padding-top: 24px;
    border-top: 1px solid var(--grey-00);
  }
  .center {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  @media only screen and (max-width: 768px) {
    padding-top: 40px;
    padding-bottom: 40px;
    .news__list {
      margin-top: 40px;
      gap: 0;
    }
  }
`;
const SearchResultEmpty = styled.div`
  width: 100%;
  height: 100%;
  min-height: 276px;
  max-height: 276px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-bottom: 1px solid var(--grey-00);
`;
export default News;
